import { useState } from "react";
import { getSupabaseClient } from "@/lib/supabase";
import { toast } from "sonner";
import { CheckIcon } from './icons';

const WEBSITE_BASE =
  import.meta.env.VITE_WEBSITE_BASE_URL ?? "http://localhost:3000";

interface TermsAcceptanceModalProps {
  onAccept: () => void;
}

export function TermsAcceptanceModal({ onAccept }: TermsAcceptanceModalProps) {
  const [agreed, setAgreed] = useState(false);

  const handleContinue = () => {
    if (!agreed) {
      toast.error(
        "Please agree to the Terms of Use and Privacy Policy to continue.",
      );
      return;
    }

    // Close modal immediately (optimistic)
    onAccept();

    // Persist to user_metadata in the background
    const supabase = getSupabaseClient();
    supabase?.auth
      .updateUser({ data: { agreed_to_terms: true } })
      .then(({ error }) => {
        if (error) console.error("Failed to save terms acceptance:", error);
      });
  };

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm" />

      {/* Modal */}
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-lg border border-white/10 bg-zinc-950 p-8 shadow-2xl">
          {/* Header */}
          <div className="mb-6 text-center">
            <h2 className="mb-2 text-xl font-medium text-white">
              Terms of Service
            </h2>
          </div>

          {/* Checkbox */}
          <div className="mb-8">
            <label className="group flex cursor-pointer items-start gap-3">
              <div className="relative mt-0.5 flex-shrink-0">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="peer sr-only"
                />
                <div className="h-[18px] w-[18px] rounded-sm border border-white/20 bg-zinc-900 transition-all duration-200 peer-checked:border-blue-400/60 peer-checked:bg-blue-400/10 peer-focus-visible:ring-1 peer-focus-visible:ring-blue-400/40" />
                <CheckIcon className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 text-blue-400 opacity-0 transition-opacity duration-200 peer-checked:opacity-100" />
              </div>
              <span className="text-[13px] leading-relaxed text-pretty text-zinc-400 transition-colors group-hover:text-zinc-200">
                I agree to Agentica's {" "}
                <a
                  href={`${WEBSITE_BASE}/terms/terms-of-use`}
                  className="underline transition-colors hover:text-white"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href={`${WEBSITE_BASE}/terms/privacy-policy`}
                  className="underline transition-colors hover:text-white"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </a>
              </span>
            </label>
          </div>

          {/* Continue button */}
          <button
            type="button"
            onClick={handleContinue}
            disabled={!agreed}
            className="w-full rounded-lg bg-white px-4 py-2.5 text-[13px] font-medium text-zinc-900 transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
          >
            Continue
          </button>
        </div>
      </div>
    </>
  );
}
